import React, { useEffect, useState } from 'react';
import {
  Col, Button, Divider, Form, Input, InputNumber, Modal, Row, Select, Tag, Upload,
} from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { useI18n } from '../i18n';
import { getDisplayNumber } from '../utils/productNumbering';

const { TextArea } = Input;
const { Option } = Select;

export default function ProductModal({
  open,
  onCancel,
  onSubmit,
  editingProduct,
  categories = [],
  suppliers = [],
  supplierProducts = [],
  documents = [],
  productType,
  // Set when opened from a Category Details Selection Wizard's Products
  // step: locks Category to the item's own category (the product wouldn't
  // qualify as a selectable row there otherwise) and requires a price so a
  // companion Supplier_Product row is always created.
  lockedCategoryId = null,
  requirePrice = false,
}) {
  const [form] = Form.useForm();
  const isEditing = Boolean(editingProduct);
  const { t } = useI18n();
  const effectiveType = editingProduct?.productType || productType || 'Standard';
  const editingSupplierProduct = editingProduct
    ? supplierProducts.find((sp) => sp.productId === editingProduct.id && sp.supplierId === editingProduct.supplierId)
    : null;
  const selectableCategories = categories.filter((category) => (
    category.status !== 'Inactive' || category.id === editingProduct?.categoryId
  ));
  const activeSuppliers = suppliers.filter((supplier) => (
    supplier.status !== 'Inactive' || supplier.id === editingProduct?.supplierId
  ));

  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [thumbnailPreviewUrl, setThumbnailPreviewUrl] = useState(null);

  useEffect(() => {
    if (!open) return;
    form.resetFields();
    form.setFieldsValue({
      name: editingProduct?.name || '',
      categoryId: editingProduct?.categoryId ?? lockedCategoryId ?? undefined,
      supplierId: editingProduct?.supplierId,
      productFamily: editingProduct?.productFamily || '',
      description: editingProduct?.description || '',
      supplierItemNumber: editingProduct?.supplierItemNumber || '',
      material: editingProduct?.material || '',
      countryOfOrigin: editingProduct?.countryOfOrigin || '',
      specifications: editingProduct?.specifications || '',
      materials: editingProduct?.materials || '',
      unitPackaging: editingProduct?.packaging?.unitPackaging || '',
      avgItemPrice: editingSupplierProduct?.avgItemPrice ?? null,
    });
    setThumbnailFile(null);
    const existingImage = editingProduct
      ? documents.find((doc) => doc.id === editingProduct.primaryImageId)
      : null;
    setThumbnailPreviewUrl(existingImage?.fileUrl || null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingProduct, form, open]);

  const handleFinish = async () => {
    const values = await form.validateFields();
    onSubmit({ ...values, productType: effectiveType, thumbnailFile });
  };

  return (
    <Modal
      title={isEditing
        ? t('Edit Product')
        : (effectiveType === 'Custom' ? t('New Custom Product') : t('New Standard Product'))}
      open={open}
      onCancel={onCancel}
      width={640}
      footer={[
        <Button key="cancel" onClick={onCancel}>{t('Close')}</Button>,
        <Button key="submit" type="primary" onClick={handleFinish}>
          {isEditing ? t('Update') : t('Create')}
        </Button>,
      ]}
    >
      <Form form={form} layout="vertical">
        <Form.Item label={t('Product Type')}>
          <Tag color={effectiveType === 'Custom' ? 'purple' : 'blue'}>
            {t(effectiveType === 'Custom' ? 'Custom Product' : 'Standard Product')}
          </Tag>
          {isEditing && (
            <span style={{ marginLeft: 8, fontSize: 12, color: '#8c8c8c' }}>
              {t('Product type cannot be changed after creation.')}
            </span>
          )}
        </Form.Item>

        {isEditing && (
          <Form.Item label={t('Product Number')}>
            <strong>{getDisplayNumber(editingProduct)}</strong>
          </Form.Item>
        )}

        <Form.Item
          label={t('Product Name')}
          name="name"
          rules={[{ required: true, message: t('Please enter product name') }]}
        >
          <Input placeholder={t('Product name')} />
        </Form.Item>

        <Form.Item label={t('Thumbnail')}>
          {thumbnailPreviewUrl && (
            <img
              src={thumbnailPreviewUrl}
              alt={t('Thumbnail')}
              style={{ display: 'block', maxWidth: 120, maxHeight: 120, marginBottom: 8, borderRadius: 6, objectFit: 'cover' }}
            />
          )}
          <Upload
            accept="image/*"
            maxCount={1}
            showUploadList={false}
            beforeUpload={(file) => {
              setThumbnailFile(file);
              setThumbnailPreviewUrl(URL.createObjectURL(file));
              return false;
            }}
          >
            <Button icon={<UploadOutlined />}>{t('Upload Photo')}</Button>
          </Upload>
        </Form.Item>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label={t('Category')}
              name="categoryId"
              rules={[{ required: true, message: t('Please select category') }]}
            >
              <Select
                showSearch
                disabled={Boolean(lockedCategoryId)}
                placeholder={t('Select active category')}
                optionFilterProp="children"
              >
                {selectableCategories.map((category) => (
                  <Option key={category.id} value={category.id}>{category.name}</Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label={t('Supplier')}
              name="supplierId"
              rules={[{ required: true, message: t('Please select supplier') }]}
            >
              <Select showSearch placeholder={t('Select supplier')} optionFilterProp="children">
                {activeSuppliers.map((supplier) => (
                  <Option key={supplier.id} value={supplier.id}>{supplier.name}</Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          {effectiveType === 'Standard' && (
            <Col span={12}>
              <Form.Item
                label={t('Supplier Item Number')}
                name="supplierItemNumber"
                extra={t('Leave blank to auto-generate an internal number instead.')}
              >
                <Input placeholder="AYA-750-BLK" />
              </Form.Item>
            </Col>
          )}
          <Col span={effectiveType === 'Standard' ? 12 : 24}>
            <Form.Item
              label={t('Average Item Price')}
              name="avgItemPrice"
              extra={t('Used as this supplier’s price in supplier comparison.')}
              rules={requirePrice ? [{ required: true, message: t('Please enter a price') }] : []}
            >
              <InputNumber min={0} step={0.01} precision={2} style={{ width: '100%' }} placeholder={t('Not provided')} addonAfter="DKK" />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item label={t('Product Family')} name="productFamily">
          <Input placeholder={t('e.g. AYA Bottle')} />
        </Form.Item>

        <Form.Item label={t('Specification')} name="description">
          <TextArea rows={2} placeholder={t('Product description')} />
        </Form.Item>

        {effectiveType === 'Standard' ? (
          <>
            <Divider orientation="left" plain>{t('Standard Product Information')}</Divider>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item label={t('Material')} name="material">
                  <Input placeholder={t('Material')} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label={t('Country of Origin')} name="countryOfOrigin">
                  <Input placeholder={t('Country of Origin')} />
                </Form.Item>
              </Col>
            </Row>
          </>
        ) : (
          <>
            <Divider orientation="left" plain>{t('Custom Product Information')}</Divider>
            <Form.Item label={t('Specifications')} name="specifications">
              <TextArea rows={2} placeholder={t('Specifications')} />
            </Form.Item>
            <Form.Item label={t('Materials')} name="materials">
              <Input placeholder={t('Materials')} />
            </Form.Item>
          </>
        )}

        <Divider orientation="left" plain>{t('Packaging')}</Divider>
        <Form.Item label={t('Unit Packaging')} name="unitPackaging">
          <Input placeholder={t('Unit Packaging')} />
        </Form.Item>
      </Form>
    </Modal>
  );
}
